import { db, type LiftXDatabase } from '../db';
import {
  DEFAULT_APPEARANCE,
  isAppearanceMode,
  type AppearanceMode,
} from '../../ui/theme/appearance';
import { systemPrefersDark } from '../../ui/theme/applyAppearance';
import { DEFAULT_ACCENT_COLOR, isAccentColor, type AccentColor } from '../../ui/theme/accentColor';

/**
 * Settings repository.
 *
 * The only code that reads or writes the `settings` table. UI components never
 * touch Dexie — Phase 0 §9 and §3.
 *
 * Every getter takes a fallback and never throws on a missing or corrupt value:
 * a settings read failing must not be able to prevent the application starting.
 */

export const SETTING_KEYS = {
  appearance: 'appearance.theme',
  /** Which of the seven `AccentColor` options replaces LiftX's default red
   *  brand accent — same generic `settings` table, same key-per-preference
   *  convention as `appearance`, deliberately not a new table (Phase 0 §9). */
  accentColor: 'appearance.accentColor',
  /**
   * When the server last confirmed this device was authenticated.
   *
   * NOT a credential and NOT a session. It cannot authenticate anything; it
   * only answers "as of when did `/api/auth/me` last say yes?", which is the
   * single input to the bounded offline grace period. The real session is the
   * `HttpOnly` cookie, which JavaScript cannot read — see
   * docs/AUTH-ARCHITECTURE.md §11.
   *
   * Same generic table as every other setting — deliberately not a separate
   * table, so it can never entangle with workout data (Phase 0 §9).
   */
  lastVerified: 'auth.lastVerified',
  /**
   * The opaque `cursor` string from the most recent successful
   * `/api/sync/pull` response — LiftX Sync Blueprint §6, §8. Feeding it back
   * on the next pull is what makes a pull incremental instead of re-fetching
   * every row every cycle. Same generic table as every other setting,
   * deliberately: this is a sync bookmark, not workout data.
   */
  syncCursor: 'sync.cursor',
  /**
   * `Date.now()` at the moment a sync cycle last reached `'synced'` —
   * `src/data/sync/syncEngine.ts`'s `runSyncCycle()` writes it. Purely a
   * display value for Settings' "Last synced" line; nothing reads it to make
   * a sync decision (that's the cursor's job, above).
   */
  lastSyncedAt: 'sync.lastSyncedAt',
  /**
   * Which account's data currently occupies this device's local data
   * tables — Phase G-A.1 ("Multi-user identity architecture" review).
   * Deliberately a SEPARATE key from `lastVerified`, not reused: the two
   * have different lifetimes. `lastVerified` must be cleared the moment the
   * server gives a definite "unauthenticated" answer (otherwise a later
   * network failure could wrongly resurrect offline access under a session
   * that's actually gone — see `revalidate()`'s own comment). This key must
   * NOT be cleared at that same moment, precisely because the data tables
   * themselves are not cleared then either — clearing this alongside
   * `lastVerified` was the original gap: it erased the one signal needed to
   * detect, once *some* account next authenticates, whether that account's
   * data actually matches what's still sitting in the local tables. It is
   * only ever updated (to the newly authenticated account) or explicitly
   * cleared once a real reset has actually run — never merely because a
   * session lapsed.
   */
  localDataOwner: 'sync.localDataOwner',
  /**
   * Durable record of whether `resetLocalDatabase()` is currently clearing
   * this device's local data tables — Phase G fix design ("cross-account
   * local-storage race"). Lives in `settings`, which `resetLocalDatabase()`
   * never itself clears, specifically so this survives a crash, a closed
   * tab, or a refresh that happens mid-reset: an in-memory-only signal (the
   * in-flight promise `resetLocalDatabase.ts` also keeps) cannot be read
   * back after the page reloads, but this can.
   *
   * Three states: `'idle'` (no reset running, or none has ever been needed
   * — the default when this key is absent) — data is trustworthy exactly as
   * far as `localDataOwner` above says it is; `'in-progress'` — a reset is
   * actively clearing tables right now, and `runSyncCycle()` must refuse to
   * push/pull regardless of `AuthProvider`'s own `status`; `'failed'` — the
   * last attempt did not complete successfully, so the table contents are in
   * an unknown, unverified state and must be treated as untrustworthy until
   * a fresh attempt succeeds. A stale `'in-progress'` found on startup (the
   * tab closed or crashed mid-reset) is never interpreted as evidence the
   * reset finished — see `resetLocalDatabase.ts`'s own doc.
   */
  localResetState: 'sync.localResetState',
} as const;

/** See `SETTING_KEYS.localResetState`'s own doc. */
export type LocalResetState = 'idle' | 'in-progress' | 'failed';

/**
 * The offline verification cache record. Deliberately minimal: an identifier,
 * a timestamp, and (Master Phase 11) the account's role, so the Settings →
 * Administration entry stays visible through the offline grace window rather
 * than flickering based on whether the last check happened to be online.
 * Nothing here is secret, and nothing here grants access — `role` is a
 * display hint only; every server-side check re-reads it fresh (§9 of the
 * approved plan). `mustChangePassword` is deliberately NOT cached here — it
 * is a short-lived, must-resolve-online transitional state, not something
 * safe to carry into an offline session.
 */
export interface LastVerifiedRecord {
  email: string;
  verifiedAt: number;
  role?: 'user' | 'admin';
}

function isLastVerifiedRecord(value: unknown): value is LastVerifiedRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  if (
    typeof record.email !== 'string' ||
    record.email.length === 0 ||
    typeof record.verifiedAt !== 'number' ||
    !Number.isFinite(record.verifiedAt)
  ) {
    return false;
  }
  return record.role === undefined || record.role === 'user' || record.role === 'admin';
}

export interface SettingsRepository {
  get<T>(key: string, fallback: T): Promise<T>;
  set(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<void>;
  all(): Promise<Record<string, unknown>>;
  getAppearance(): Promise<AppearanceMode>;
  setAppearance(mode: AppearanceMode): Promise<void>;
  /** Unlike `getAppearance()`, an invalid/absent stored value never migrates
   *  or writes anything — it simply reads back as the default, matching
   *  "no preference exists" behaving exactly like today's red-only app. */
  getAccentColor(): Promise<AccentColor>;
  setAccentColor(color: AccentColor): Promise<void>;
  getLastVerified(): Promise<LastVerifiedRecord | null>;
  setLastVerified(record: LastVerifiedRecord): Promise<void>;
  clearLastVerified(): Promise<void>;
  /**
   * Drops the saved sync pull cursor. Called alongside `clearLastVerified()`
   * at the same two moments (an explicit logout, and a definite server "no"
   * discovered without one) — `db` is one shared Dexie instance, not
   * partitioned per account, so a leftover cursor from the previous account
   * would otherwise be read by whichever account signs in next. Because the
   * server always scopes a pull by the authenticated session's `user_id`,
   * this was never a cross-account data leak — but the leftover cursor's
   * raw timestamp would still be applied as a position filter within the
   * new account's own rows, which can cause it to silently skip its own
   * data older than that timestamp on its first pull.
   */
  clearSyncCursor(): Promise<void>;
  /** See `SETTING_KEYS.localDataOwner`'s own doc for why this is a separate lifetime from `lastVerified`. */
  getLocalDataOwner(): Promise<LocalDataOwnerRecord | null>;
  setLocalDataOwner(record: LocalDataOwnerRecord): Promise<void>;
  /** See `SETTING_KEYS.localResetState`'s own doc. Absent reads as `'idle'`; any unrecognised stored value reads as `'failed'` (fail closed, not fail open). */
  getLocalResetState(): Promise<LocalResetState>;
  setLocalResetState(state: LocalResetState): Promise<void>;
}

/** See `SETTING_KEYS.localDataOwner`'s own doc. */
export interface LocalDataOwnerRecord {
  email: string;
}

function isLocalDataOwnerRecord(value: unknown): value is LocalDataOwnerRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.email === 'string' && record.email.length > 0;
}

export function createSettingsRepository(database: LiftXDatabase = db): SettingsRepository {
  return {
    async get<T>(key: string, fallback: T): Promise<T> {
      const row = await database.settings.get(key);
      if (row === undefined || row.value === undefined || row.value === null) return fallback;
      return row.value as T;
    },

    async set(key: string, value: unknown): Promise<void> {
      await database.settings.put({ key, value, updatedAt: Date.now() });
    },

    async remove(key: string): Promise<void> {
      await database.settings.delete(key);
    },

    async all(): Promise<Record<string, unknown>> {
      const rows = await database.settings.toArray();
      return Object.fromEntries(rows.map((row) => [row.key, row.value]));
    },

    async getAppearance(): Promise<AppearanceMode> {
      const raw = await this.get<unknown>(SETTING_KEYS.appearance, DEFAULT_APPEARANCE);
      if (isAppearanceMode(raw)) return raw;
      // Anything else — a hand-edited value, or (the real case in practice) a
      // pre-upgrade `system` preference from before that option was removed —
      // migrates once, here, to a concrete choice resolved from the device's
      // current preference, and is persisted immediately so every later read
      // is already stable rather than re-resolving on every launch.
      const migrated: AppearanceMode = systemPrefersDark() ? 'dark' : 'light';
      await this.setAppearance(migrated);
      return migrated;
    },

    async setAppearance(mode: AppearanceMode): Promise<void> {
      await this.set(SETTING_KEYS.appearance, mode);
    },

    async getAccentColor(): Promise<AccentColor> {
      const raw = await this.get<unknown>(SETTING_KEYS.accentColor, DEFAULT_ACCENT_COLOR);
      return isAccentColor(raw) ? raw : DEFAULT_ACCENT_COLOR;
    },

    async setAccentColor(color: AccentColor): Promise<void> {
      await this.set(SETTING_KEYS.accentColor, color);
    },

    async getLastVerified(): Promise<LastVerifiedRecord | null> {
      const raw = await this.get<unknown>(SETTING_KEYS.lastVerified, null);
      // A hand-edited, partially-restored, or truncated value must read as
      // "never verified" — the closed state — rather than throwing or being
      // trusted in a malformed shape.
      if (!isLastVerifiedRecord(raw)) return null;
      return raw.role === undefined
        ? { email: raw.email, verifiedAt: raw.verifiedAt }
        : { email: raw.email, verifiedAt: raw.verifiedAt, role: raw.role };
    },

    async setLastVerified(record: LastVerifiedRecord): Promise<void> {
      await this.set(SETTING_KEYS.lastVerified, {
        email: record.email,
        verifiedAt: record.verifiedAt,
        ...(record.role !== undefined ? { role: record.role } : {}),
      });
    },

    async clearLastVerified(): Promise<void> {
      // Removed rather than blanked: logging out should leave no trace of a
      // prior verification in the store, not a stale marker.
      await this.remove(SETTING_KEYS.lastVerified);
    },

    async clearSyncCursor(): Promise<void> {
      await this.remove(SETTING_KEYS.syncCursor);
    },

    async getLocalDataOwner(): Promise<LocalDataOwnerRecord | null> {
      const raw = await this.get<unknown>(SETTING_KEYS.localDataOwner, null);
      return isLocalDataOwnerRecord(raw) ? raw : null;
    },

    async setLocalDataOwner(record: LocalDataOwnerRecord): Promise<void> {
      await this.set(SETTING_KEYS.localDataOwner, record);
    },

    async getLocalResetState(): Promise<LocalResetState> {
      const row = await database.settings.get(SETTING_KEYS.localResetState);
      if (row === undefined || row.value === undefined || row.value === null) return 'idle';
      if (row.value === 'idle' || row.value === 'in-progress' || row.value === 'failed') return row.value;
      return 'failed'; // present but unrecognised — fail closed, not open.
    },

    async setLocalResetState(state: LocalResetState): Promise<void> {
      await this.set(SETTING_KEYS.localResetState, state);
    },
  };
}

/** The application-wide instance. Tests build their own against a test database. */
export const settingsRepository = createSettingsRepository();
